import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Mail } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { 
  Video, 
  Mic, 
  MicOff, 
  VideoOff, 
  AlertTriangle, 
  CheckCircle2, 
  Info,
  Loader2,
  ArrowRight,
  Upload,
  Clock,
  Shield,
  Volume2,
  VolumeX,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { InterviewTipsCard } from "@/components/InterviewTipsCard";

interface InviteData {
  valid: boolean;
  status: string;
  error?: string;
  roleTitle?: string;
  companyName?: string;
  companyLogoUrl?: string | null;
  questions?: Array<{
    id: string;
    questionText: string;
    questionType: string;
    timeLimit?: number;
  }>;
  canResume?: boolean;
  currentQuestion?: number;
  interviewId?: number;
  applicationId?: number;
}

function generateFingerprint(): string {
  const nav = window.navigator;
  const screen = window.screen;
  const components = [
    nav.userAgent,
    nav.platform,
    nav.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset()
  ];
  return components.join('|');
}

export default function InterviewInvite() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [consentGiven, setConsentGiven] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);
  const [micPermission, setMicPermission] = useState<boolean | null>(null);
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showTipsCard, setShowTipsCard] = useState<boolean>(
    () => localStorage.getItem("vexis_tips_shown") !== "true"
  );
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlobs, setRecordedBlobs] = useState<Blob[]>([]);
  const [uploadedQuestions, setUploadedQuestions] = useState<Set<number>>(new Set());
  const [repeatedQuestions, setRepeatedQuestions] = useState<Set<number>>(new Set());
  const [showingRepeat, setShowingRepeat] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [audioNarrateLoading, setAudioNarrateLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioAutoplayBlocked, setAudioAutoplayBlocked] = useState(false);
  const [ttsUnavailable, setTtsUnavailable] = useState(false);
  const [recordingTimeLeft, setRecordingTimeLeft] = useState<number | null>(null);

  const [recordingError, setRecordingError] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const fingerprintRef = useRef<string>("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCache = useRef<Map<string, string>>(new Map());
  const audioQuestionIndexRef = useRef<number>(-1); // Track which question the audio is for
  const recordingQuestionIndexRef = useRef<number>(-1); // Track which question the recording is for
  const mimeTypeRef = useRef<string>("video/webm"); // Track the resolved MIME type for blob + upload

  // Web Audio API — unlocked during user gesture so async play() always works (including iOS Safari)
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferCacheRef = useRef<Map<string, AudioBuffer>>(new Map());
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);

  // TTS narration guards — scoped per question index, never gate recording
  const questionNarratedRef = useRef<Set<number>>(new Set()); // Questions where TTS has been attempted
  const ttsInProgressRef = useRef<boolean>(false); // Synchronous guard against concurrent TTS calls
  // Per-question recording initiation guard — recording is fully independent of TTS
  const recordingInitiatedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    fingerprintRef.current = generateFingerprint();
    resolveInvite();
  }, [token]);

  // Re-attach stream to video element when it changes (e.g., after interview starts)
  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [interviewStarted, cameraPermission]);

  const resolveInvite = async () => {
    try {
      const response = await fetch(`/api/invites/resolve?token=${encodeURIComponent(token || "")}`);
      const data = await response.json();
      setInviteData(data);
      
      if (data.valid && data.canResume) {
        setShowTipsCard(false); // Skip tips on resume — candidate has already started
        setInterviewStarted(true);
        const resumeIndex = data.currentQuestion || 0;
        setCurrentQuestionIndex(resumeIndex);
        // Pre-populate uploadedQuestions so the progress bar and allUploaded check
        // reflect questions already answered server-side before this session started.
        if (resumeIndex > 0) {
          setUploadedQuestions(new Set(Array.from({ length: resumeIndex }, (_, i) => i)));
        }
        await setupMedia();
      }
    } catch (error) {
      setInviteData({ valid: false, status: "error", error: "Failed to validate interview link" });
    } finally {
      setLoading(false);
    }
  };

  const setupMedia = async () => {
    try {
      console.log("[Media] Requesting camera and microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      console.log("[Media] Access granted, stream tracks:", stream.getTracks().map(t => `${t.kind}:${t.label}`));
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        console.log("[Media] Stream attached to video element");
      }
      setCameraPermission(true);
      setMicPermission(true);
      return true;
    } catch (error: any) {
      console.error("[Media] Access error:", error.name, error.message);
      setCameraPermission(false);
      setMicPermission(false);
      return false;
    }
  };

  // Must be called synchronously during a user-gesture handler (before any await).
  // Creates and resumes an AudioContext so that all subsequent Web Audio playback
  // succeeds regardless of async delays — this is the only reliable approach on iOS Safari.
  const unlockAudioContext = () => {
    try {
      const AudioCtx = (window as any).AudioContext ?? (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioCtx() as AudioContext;
      }
      if (audioContextRef.current.state === "suspended") {
        audioContextRef.current.resume().catch(() => {});
      }
    } catch {
      // AudioContext not available — HTMLAudio fallback will be used
    }
  };


  const playQuestionAudio = useCallback(async (questionText: string, questionIndex: number, questionId?: string) => {
    // CRITICAL GUARD 1: Synchronous check to prevent race conditions
    if (ttsInProgressRef.current) {
      console.log("[Audio] BLOCKED: TTS already in progress, ignoring duplicate call for question", questionIndex);
      return;
    }
    
    // CRITICAL GUARD 2: Check if TTS has already been attempted for this question
    if (questionNarratedRef.current.has(questionIndex)) {
      console.log("[Audio] BLOCKED: TTS already attempted for question", questionIndex, "- skipping duplicate");
      return;
    }
    
    // Set synchronous guard IMMEDIATELY before any async work
    ttsInProgressRef.current = true;
    questionNarratedRef.current.add(questionIndex);
    
    try {
      console.log("[Audio] Starting ElevenLabs narration for question", questionIndex, ":", questionText.substring(0, 50) + "...");

      audioQuestionIndexRef.current = questionIndex;
      setIsPlayingAudio(true);
      setAudioReady(false);

      const cacheKey = questionText;
      const capturedQuestionIndex = questionIndex;

      // Shared onended handler — manages audio state only; recording runs independently
      const handleEnded = () => {
        ttsInProgressRef.current = false;
        if (audioQuestionIndexRef.current !== capturedQuestionIndex) {
          console.log("[Audio] Ignoring stale onended for question", capturedQuestionIndex, "(current:", audioQuestionIndexRef.current, ")");
          return;
        }
        console.log("[Audio] Playback ended for question", capturedQuestionIndex);
        setIsPlayingAudio(false);
        setAudioReady(true);
      };

      // ── PATH A: Web Audio API (preferred — works on iOS Safari and all modern browsers) ──
      const ctx = audioContextRef.current;
      if (ctx) {
        // Resume in case the context was suspended after an inactivity pause
        if (ctx.state === "suspended") await ctx.resume();

        let audioBuffer = audioBufferCacheRef.current.get(cacheKey);
        if (!audioBuffer) {
          console.log("[Audio] Fetching narration from ElevenLabs (Web Audio path)...");
          setAudioNarrateLoading(true);
          const response = await fetch("/api/interview/narrate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text: questionText, questionId }),
          });
          setAudioNarrateLoading(false);

          if (!response.ok) {
            const errorText = await response.text();
            console.warn("[Audio] ElevenLabs narrate failed:", response.status, errorText);
            throw new Error("ElevenLabs unavailable");
          }

          const arrayBuffer = await response.arrayBuffer();
          console.log("[Audio] Received ElevenLabs audio, bytes:", arrayBuffer.byteLength);
          audioBuffer = await ctx.decodeAudioData(arrayBuffer);
          audioBufferCacheRef.current.set(cacheKey, audioBuffer);
        } else {
          console.log("[Audio] Using cached AudioBuffer for question", questionIndex);
        }

        // Stop any source that's still running
        if (currentSourceRef.current) {
          try { currentSourceRef.current.stop(); } catch { /* already stopped */ }
          currentSourceRef.current = null;
        }

        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);
        currentSourceRef.current = source;
        source.onended = handleEnded;
        source.start(0);
        console.log("[Audio] Web Audio playback started for question", questionIndex);
        setAudioAutoplayBlocked(false);
        return;
      }

      // ── PATH B: HTMLAudioElement fallback (desktop browsers without AudioContext) ──
      let audioUrl = audioCache.current.get(cacheKey);
      if (!audioUrl) {
        console.log("[Audio] Fetching narration from ElevenLabs (HTMLAudio path)...");
        setAudioNarrateLoading(true);
        const response = await fetch("/api/interview/narrate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: questionText, questionId }),
        });
        setAudioNarrateLoading(false);

        if (!response.ok) {
          const errorText = await response.text();
          console.warn("[Audio] ElevenLabs narrate failed:", response.status, errorText);
          throw new Error("ElevenLabs unavailable");
        }

        const audioBlob = await response.blob();
        console.log("[Audio] Received ElevenLabs audio blob, size:", audioBlob.size);
        audioUrl = URL.createObjectURL(audioBlob);
        audioCache.current.set(cacheKey, audioUrl);
      } else {
        console.log("[Audio] Using cached object URL for question", questionIndex);
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onended = handleEnded;
      audio.onerror = (e) => {
        ttsInProgressRef.current = false;
        if (audioQuestionIndexRef.current !== capturedQuestionIndex) return;
        console.error("[Audio] HTMLAudio playback error:", e);
        // Recording runs independently — do not gate it on audio state
        setIsPlayingAudio(false);
        setAudioReady(true);
      };

      try {
        await audio.play();
        console.log("[Audio] HTMLAudio playback started for question", questionIndex);
        setAudioAutoplayBlocked(false);
      } catch (playError: any) {
        // Autoplay was blocked by the browser — show the "Click to play" prompt.
        // Recording is unaffected — it starts independently of TTS.
        console.warn("[Audio] Autoplay blocked by browser:", playError.message);
        ttsInProgressRef.current = false;
        setIsPlayingAudio(false);
        setAudioReady(true);
        setAudioAutoplayBlocked(true);
      }
    } catch (error: any) {
      // ElevenLabs call failed (network error, 4xx, 5xx).
      // Show a clear message. Recording is fully independent — it starts
      // immediately on question load and is unaffected by TTS failure.
      console.error("[TTS] ElevenLabs failed, reason:", error?.status ?? "unknown", error?.message ?? error);
      setAudioNarrateLoading(false);
      ttsInProgressRef.current = false;
      setIsPlayingAudio(false);
      setAudioReady(true);
      setTtsUnavailable(true);
    }
  }, []);

  // Manual replay — uses cached AudioBuffer (Web Audio) or HTMLAudio element, no re-fetch needed
  const replayQuestionAudio = useCallback((questionText: string, questionIndex: number, questionId?: string) => {
    console.log("[Audio] Manual replay triggered for question", questionIndex);

    // ── Web Audio replay (preferred) ──────────────────────────────────────────
    const ctx = audioContextRef.current;
    const cachedBuffer = audioBufferCacheRef.current.get(questionText);
    if (ctx && cachedBuffer) {
      if (currentSourceRef.current) {
        try { currentSourceRef.current.stop(); } catch { /* already stopped */ }
        currentSourceRef.current = null;
      }
      const source = ctx.createBufferSource();
      source.buffer = cachedBuffer;
      source.connect(ctx.destination);
      currentSourceRef.current = source;
      source.onended = () => {
        ttsInProgressRef.current = false;
        setIsPlayingAudio(false);
        setAudioReady(true);
      };
      setIsPlayingAudio(true);
      source.start(0);
      return;
    }

    // ── HTMLAudio replay ───────────────────────────────────────────────────────
    if (audioRef.current && audioQuestionIndexRef.current === questionIndex) {
      audioRef.current.currentTime = 0;
      setIsPlayingAudio(true);
      audioRef.current.play().catch((e) => {
        console.warn("[Audio] HTMLAudio replay blocked:", e.message);
        setIsPlayingAudio(false);
        setAudioAutoplayBlocked(true);
      });
      return;
    }

    // ── Re-fetch (cache miss — shouldn't happen in normal flow) ───────────────
    questionNarratedRef.current.delete(questionIndex);
    ttsInProgressRef.current = false;
    playQuestionAudio(questionText, questionIndex, questionId);
  }, [playQuestionAudio]);

  // Fire TTS in parallel with recording — fully independent of recording state.
  // Recording starts immediately via its own effect below; TTS fires here concurrently.
  useEffect(() => {
    const isUploaded = uploadedQuestions.has(currentQuestionIndex);
    if (interviewStarted &&
        !showTipsCard &&
        inviteData?.questions?.[currentQuestionIndex] &&
        !isUploaded &&
        !uploading &&
        !isPlayingAudio) {
      const question = inviteData.questions[currentQuestionIndex];
      playQuestionAudio(question.questionText, currentQuestionIndex, question.id);
    }
  }, [interviewStarted, showTipsCard, currentQuestionIndex, inviteData, playQuestionAudio, uploadedQuestions, uploading, isPlayingAudio]);

  // Preload next question's audio while candidate is recording current answer
  useEffect(() => {
    if (!isRecording || !inviteData?.questions) return;
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= inviteData.questions.length) return;
    const nextQ = inviteData.questions[nextIndex];
    if (!nextQ || audioCache.current.has(nextQ.questionText)) return;
    // Silently pre-fetch so there's no delay when moving to the next question
    fetch("/api/interview/narrate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: nextQ.questionText, questionId: nextQ.id }),
    })
      .then(async (res) => {
        if (!res.ok) return;
        const ctx = audioContextRef.current;
        if (ctx) {
          // Decode directly into an AudioBuffer so the Web Audio path has it ready
          const arrayBuffer = await res.arrayBuffer();
          try {
            const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
            audioBufferCacheRef.current.set(nextQ.questionText, audioBuffer);
            console.log("[Audio] Pre-decoded AudioBuffer for question", nextIndex);
          } catch {
            // Decoding failed — fall through; the play path will re-fetch if needed
          }
        } else {
          // AudioContext not available — cache as object URL for HTMLAudio fallback
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          audioCache.current.set(nextQ.questionText, url);
          console.log("[Audio] Pre-fetched narration (URL) for question", nextIndex);
        }
      })
      .catch(() => {/* silent — preload is best-effort */});
  }, [isRecording, currentQuestionIndex, inviteData]);

  useEffect(() => {
    return () => {
      // Revoke any object URLs held in the HTMLAudio cache
      audioCache.current.forEach((url) => URL.revokeObjectURL(url));
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Stop any running Web Audio source node
      if (currentSourceRef.current) {
        try { currentSourceRef.current.stop(); } catch { /* already stopped */ }
      }
      // Close the AudioContext to release OS audio resources
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  const checkPermissions = async () => {
    const success = await setupMedia();
    if (!success) {
      toast({
        variant: "destructive",
        title: "Camera/Microphone Required",
        description: "Please allow access to your camera and microphone to continue.",
      });
    }
  };

  const claimInvite = async () => {
    // Unlock Web Audio API synchronously during this user gesture, before any async calls.
    // This is the ONLY reliable way to allow programmatic audio playback on iOS Safari.
    unlockAudioContext();

    if (!candidateName.trim() || !candidateEmail.trim()) {
      toast({
        variant: "destructive",
        title: "Information Required",
        description: "Please enter your full name and email address.",
      });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(candidateEmail)) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address.",
      });
      return;
    }
    
    if (!consentGiven) {
      toast({
        variant: "destructive",
        title: "Consent Required",
        description: "Please agree to the recording consent to continue.",
      });
      return;
    }

    setClaiming(true);
    try {
      // First claim the invite to reserve it, then setup media
      console.log("[Interview] Claiming invite with token:", token?.substring(0, 8) + "...");
      const response = await fetch("/api/invites/claim", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          fingerprint: fingerprintRef.current,
          candidateName: candidateName.trim(),
          candidateEmail: candidateEmail.trim(),
        }),
      });

      const data = await response.json();
      console.log("[Interview] Claim response:", response.status, data);
      
      if (!response.ok) {
        console.error("[Interview] Claim failed:", data.error);
        toast({
          variant: "destructive",
          title: "Unable to Start Interview",
          description: data.error || "This link may have already been used.",
        });
        setInviteData({ valid: false, status: "claimed", error: data.error });
        return;
      }

      console.log("[Interview] Interview claimed successfully");
      
      // Now setup media after successful claim
      console.log("[Interview] Setting up media after claim...");
      const mediaSuccess = await setupMedia();
      
      if (!mediaSuccess) {
        toast({
          variant: "destructive",
          title: "Camera/Microphone Required",
          description: "Please allow camera and microphone access when prompted, then click 'Resume Interview' to continue.",
        });
        // Update invite data to show resume state
        setInviteData(prev => prev ? { ...prev, status: "claimed", canResume: true } : prev);
        return;
      }
      
      setInterviewStarted(true);
      toast({
        title: "Interview Started",
        description: "Good luck! Answer each question by recording a video response.",
      });
    } catch (error) {
      console.error("[Interview] Claim error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to start interview. Please try again.",
      });
    } finally {
      setClaiming(false);
    }
  };

  const startRecording = useCallback(async (forQuestionIndex: number) => {
    // Tear down any previous recorder instance — never reuse a stopped MediaRecorder.
    // Silence onstop FIRST (before stop()) so any pending onstop event from the old
    // recorder is a no-op, regardless of whether the state is still 'recording' or
    // already 'inactive'. This prevents a race where fast-cached audio causes
    // startRecording(N+1) to run before question N's onstop fires, which would have
    // caused the stale-index guard to wrongly discard question N's blob.
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = null; // silence before teardown
      if (mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch {}
      }
      mediaRecorderRef.current = null;
    }

    setRecordingError(null);

    // Check if stream tracks are still live; Safari/iOS can mark them ended after stop()
    let stream = streamRef.current;
    if (!stream) {
      console.error("[Recording] No stream available");
      setRecordingError("Something went wrong — please refresh and try again.");
      return;
    }

    const hasEndedTracks = stream.getTracks().some(t => t.readyState === 'ended');
    if (hasEndedTracks) {
      console.warn("[Recording] Stream tracks ended after previous recording — re-acquiring media...");
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        stream.getTracks().forEach(t => { try { t.stop(); } catch {} });
        streamRef.current = newStream;
        stream = newStream;
        if (videoRef.current) videoRef.current.srcObject = newStream;
        console.log("[Recording] Stream re-acquired successfully");
      } catch (mediaErr: any) {
        console.error("[Recording] Failed to re-acquire stream:", mediaErr.message);
        setRecordingError("Something went wrong — please refresh and try again.");
        return;
      }
    }

    console.log("[Recording] Starting recording for question", forQuestionIndex);
    chunksRef.current = [];
    recordingQuestionIndexRef.current = forQuestionIndex;

    // Priority-ordered MIME type list — always include an audio codec.
    // video/mp4;codecs=avc1,mp4a.40.2 covers iOS Safari (H.264 + AAC-LC).
    // Bare "video/mp4;codecs=avc1" deliberately omitted — it records video-only.
    const mimeTypeCandidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=vp8,mp4a.40.2",
      "video/webm",
      "video/mp4;codecs=avc1,mp4a.40.2",
      "video/mp4",
    ];
    const mimeType = mimeTypeCandidates.find(type => MediaRecorder.isTypeSupported(type)) || "";
    if (!mimeType) {
      console.error("[Recording] No supported MIME type found on this device — recording will likely fail");
    } else {
      console.log("[Recording] Using MIME type:", mimeType);
    }
    mimeTypeRef.current = mimeType || "video/webm";

    // Capture the question index for the callback closure
    const capturedQuestionIndex = forQuestionIndex;

    try {
      // Use low bitrate to keep file sizes small (500kbps video + 64kbps audio = ~2MB for 30s)
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 500000,  // 500 kbps video
        audioBitsPerSecond: 64000    // 64 kbps audio
      });

      // Guard: recorder must be inactive before start() — throw rather than silently fail
      if (mediaRecorder.state !== 'inactive') {
        throw new Error(`MediaRecorder not inactive after construction (state: ${mediaRecorder.state})`);
      }

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          console.log("[Recording] Chunk received, size:", event.data.size);
        }
      };

      mediaRecorder.onstop = () => {
        // Ignore stale callback if we've moved to a different question
        if (recordingQuestionIndexRef.current !== capturedQuestionIndex) {
          console.log("[Recording] Ignoring stale onstop for question", capturedQuestionIndex, "(current:", recordingQuestionIndexRef.current, ")");
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        console.log("[DEBUG] onstop fired, blob size:", blob.size, "chunks:", chunksRef.current.length, "question:", capturedQuestionIndex);
        console.log("[Recording] Stopped for question", capturedQuestionIndex, ", MIME:", mimeTypeRef.current, ", total blob size:", blob.size);
        setRecordedBlobs(prev => [...prev, blob]);
      };

      mediaRecorder.onerror = (event: any) => {
        console.error("[Recording] MediaRecorder error:", event.error);
        setRecordingError("Something went wrong — please refresh and try again.");
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setIsRecording(true);
      console.log("[Recording] Recording started");
    } catch (error) {
      console.error("[Recording] Failed to start:", error);
      setRecordingError("Something went wrong — please refresh and try again.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    console.log("[Recording] Stop requested, isRecording:", isRecording);
    if (mediaRecorderRef.current && isRecording) {
      console.log("[Recording] Stopping MediaRecorder...");
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null; // tear down — onstop still fires via its closure
      setIsRecording(false);
    }
  }, [isRecording]);

  // Start recording IMMEDIATELY when a question loads — independent of TTS.
  // Recording never waits for audio to play; TTS fires in parallel via its own effect.
  useEffect(() => {
    if (!interviewStarted || showTipsCard) return;
    if (!inviteData?.questions?.[currentQuestionIndex]) return;
    if (!streamRef.current) return;
    const isUploaded = uploadedQuestions.has(currentQuestionIndex);
    if (isUploaded || uploading || isRecording || recordedBlobs.length > 0) return;
    // Per-question guard: only initiate once per question index
    if (recordingInitiatedRef.current.has(currentQuestionIndex)) return;
    recordingInitiatedRef.current.add(currentQuestionIndex);
    const question = inviteData.questions[currentQuestionIndex];
    const timeLimit = question.timeLimit || 30;
    console.log("[Auto-Record] Starting recording immediately for question", currentQuestionIndex, "time limit:", timeLimit);
    setRecordingTimeLeft(timeLimit);
    startRecording(currentQuestionIndex);
  }, [interviewStarted, showTipsCard, currentQuestionIndex, inviteData, uploadedQuestions, uploading, isRecording, recordedBlobs, startRecording]);

  // Countdown timer during recording
  useEffect(() => {
    if (isRecording && recordingTimeLeft !== null && recordingTimeLeft > 0) {
      const timer = setTimeout(() => {
        setRecordingTimeLeft(prev => prev !== null ? prev - 1 : null);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isRecording && recordingTimeLeft === 0) {
      console.log("[Auto-Record] Time limit reached, stopping recording");
      stopRecording();
    }
  }, [isRecording, recordingTimeLeft, stopRecording]);

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadAttempts, setUploadAttempts] = useState(0);
  const maxRetries = 3;

  // Capture the current question index at upload time so the loop closure is stable.
  // Using a ref avoids stale-closure issues when React batches state updates mid-loop.
  const uploadQuestionIndexRef = useRef<number>(0);

  const uploadResponse = async () => {
    const blob = recordedBlobs[recordedBlobs.length - 1];
    if (!blob || !inviteData?.questions) {
      console.error("[Interview] Upload called but no blob or questions available");
      return;
    }

    if (blob.size === 0) {
      console.error("[Interview] Upload called with empty blob — prompting re-record");
      setRecordedBlobs([]);
      setRecordingError("Your recording came through empty — please record your answer again.");
      // Clear the per-question initiation guard so the auto-record effect can restart
      recordingInitiatedRef.current.delete(currentQuestionIndex);
      toast({
        variant: "destructive",
        title: "Recording Error",
        description: "Your recording was empty. Recording will restart automatically.",
      });
      return;
    }

    // Snapshot the question index for this upload session
    const questionIdx = currentQuestionIndex;
    uploadQuestionIndexRef.current = questionIdx;

    setUploading(true);
    setUploadError(null);

    const uploadMimeType = mimeTypeRef.current;
    const fileExt = uploadMimeType.includes("mp4") ? "mp4" : "webm";
    let lastErrorMessage = "Failed to save your response";

    try {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        setUploadAttempts(attempt + 1);
        console.log("[Interview] Uploading response for question:", questionIdx, "blob size:", blob.size, "mimeType:", uploadMimeType, "attempt:", attempt + 1);

        if (attempt > 0) {
          toast({
            title: "Retrying Upload...",
            description: `Attempt ${attempt + 1} of ${maxRetries}`,
          });
          await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempt - 1)));
        }

        try {
          const formData = new FormData();
          formData.append("video", blob, `response_q${questionIdx}.${fileExt}`);
          formData.append("token", token || "");
          formData.append("fingerprint", fingerprintRef.current);
          formData.append("questionIndex", questionIdx.toString());
          formData.append("questionId", inviteData.questions[questionIdx].id);
          formData.append("mimeType", uploadMimeType);

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 180000);

          const response = await fetch(`/api/invites/${inviteData.interviewId}/upload`, {
            method: "POST",
            body: formData,
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          let data: any;
          try {
            data = await response.json();
          } catch (e) {
            console.error("[Interview] Failed to parse response JSON:", e);
            throw new Error("Server returned invalid response");
          }

          console.log("[Interview] Upload response:", response.status, data);

          if (!response.ok) {
            console.error("[Interview] Upload failed:", data.error);
            throw new Error(data.error || `Upload failed with status ${response.status}`);
          }

          // ── SUCCESS ──
          console.log("[DEBUG] upload result: SUCCESS — question", questionIdx, "attempt", attempt + 1);
          console.log("[Interview] Video uploaded successfully for question", questionIdx);
          setUploadedQuestions(prev => {
            const newSet = new Set(prev);
            newSet.add(questionIdx);
            return newSet;
          });
          setUploadError(null);
          setUploadAttempts(0);

          toast({
            title: "Response Saved",
            description: "Your answer has been recorded successfully.",
          });

          if (questionIdx < inviteData.questions.length - 1) {
            console.log("[DEBUG] advancing to question:", questionIdx + 1, "(total questions:", inviteData.questions.length, ")");
            setRecordedBlobs([]);
            setRecordingTimeLeft(null);
            setAudioAutoplayBlocked(false);
            setAudioReady(false);
            setAudioNarrateLoading(false);
            setTtsUnavailable(false);
            setRecordingError(null);
            audioQuestionIndexRef.current = -1;
            recordingQuestionIndexRef.current = -1;
            setCurrentQuestionIndex(prev => prev + 1);
          } else {
            console.log("[DEBUG] Q", questionIdx, "is the LAST question — no advance, showing submit");
          }
          return; // exit loop — upload succeeded
        } catch (attemptError: any) {
          console.error(`[Interview] Upload attempt ${attempt + 1} error:`, attemptError);
          lastErrorMessage = attemptError.name === "AbortError"
            ? "Upload timed out. Please check your internet connection."
            : attemptError.message || "Failed to save your response";
          console.log(`[DEBUG] upload result: FAIL — question ${questionIdx} attempt ${attempt + 1}/${maxRetries}: ${lastErrorMessage}`);
        }
      }

      // All attempts exhausted
      setUploadError(lastErrorMessage);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: lastErrorMessage + " Tap 'Retry Upload' to try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  const retryUpload = () => {
    setUploadError(null);
    uploadResponse();
  };

  // Auto-upload when recording stops (blob is ready)
  useEffect(() => {
    const hasRecording = recordedBlobs.length > 0 && recordedBlobs[recordedBlobs.length - 1]?.size > 0;
    const isUploaded = uploadedQuestions.has(currentQuestionIndex);
    
    // Don't auto-retry if there's already an upload error (user must manually retry)
    if (hasRecording && !isUploaded && !uploading && !isRecording && !uploadError) {
      console.log("[Auto-Record] Recording stopped, auto-uploading...");
      uploadResponse();
    }
  }, [recordedBlobs, uploadedQuestions, currentQuestionIndex, uploading, isRecording, uploadError]);

  const completeInterview = async () => {
    const totalQuestions = inviteData?.questions?.length ?? 0;
    const lastIdx = Math.max(0, totalQuestions - 1);
    const lastQuestionUploaded = uploadedQuestions.has(lastIdx) && currentQuestionIndex >= lastIdx;
    if (!inviteData?.questions || !lastQuestionUploaded) {
      toast({
        variant: "destructive",
        title: "Incomplete Interview",
        description: "Please answer all questions before submitting.",
      });
      return;
    }

    try {
      console.log("[Interview] Submitting interview completion for interviewId:", inviteData.interviewId);
      const response = await fetch(`/api/invites/${inviteData.interviewId}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          fingerprint: fingerprintRef.current,
        }),
      });

      let data: any = {};
      try {
        data = await response.json();
      } catch {
        if (!response.ok) throw new Error(`Server error (${response.status})`);
      }
      console.log("[Interview] Complete response:", response.status, data);

      if (!response.ok) {
        if (data.currentStatus === "used" || data.alreadyCompleted) {
          navigate(`/interview-complete?t=${encodeURIComponent(token || "")}`);
          return;
        }
        // 422 = server detected incomplete responses despite client-side check
        if (response.status === 422) {
          const answered = data.answeredCount ?? 0;
          const total = data.totalQuestions ?? "?";
          throw new Error(`${data.error || "Not all questions answered"} (${answered}/${total} uploaded). Please wait for uploads to finish and try again.`);
        }
        throw new Error(data.error || `Submission failed (${response.status})`);
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      navigate(`/interview-complete?t=${encodeURIComponent(token || "")}`);
    } catch (error: any) {
      console.error("[Interview] Complete error:", error);
      if (error.message?.includes("already") || error.message?.includes("used")) {
        navigate(`/interview-complete?t=${encodeURIComponent(token || "")}`);
        return;
      }
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: error.message || "Failed to submit interview. Please try again.",
      });
    }
  };

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950" data-testid="loading-screen">
        <div className="text-center">
          <img src="/vexis-logo.png" alt="VEXIS" className="h-14 mx-auto mb-6" />
          <Loader2 className="h-10 w-10 animate-spin text-blue-500 mx-auto" />
          <p className="mt-4 text-slate-400">Validating interview link...</p>
        </div>
      </div>
    );
  }

  if (!inviteData?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4" data-testid="invalid-link-screen">
        <div className="max-w-md w-full bg-slate-900 border border-slate-700 rounded-xl p-6">
          <div className="text-center mb-6">
            <img src="/vexis-logo.png" alt="VEXIS" className="h-12 mx-auto mb-4" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-8 w-8 text-red-400" />
            <h2 className="text-xl font-bold text-white">
              {inviteData?.status === "used" || inviteData?.status === "claimed"
                ? "You have already completed this interview"
                : inviteData?.status === "expired"
                ? "This link has expired"
                : "Interview link unavailable"}
            </h2>
          </div>
          <p className="text-slate-300 mb-4">
            {inviteData?.status === "used" || inviteData?.status === "claimed"
              ? `Your interview${inviteData?.roleTitle ? ` for ${inviteData.roleTitle}` : ""} was submitted successfully. Thank you - you will hear back within the timeframe specified by the employer.`
              : inviteData?.status === "expired"
              ? `Your interview link${inviteData?.roleTitle ? ` for ${inviteData.roleTitle}` : ""} is no longer active. Please contact ${inviteData?.companyName || "the company"} to request a new link - they can reissue one in under a minute.`
              : inviteData?.status === "revoked"
              ? "This interview link has been revoked. Please contact the company for assistance."
              : "This interview link is invalid or has already been used. Please contact the company for a new link."}
          </p>
          <div className="text-center text-xs text-slate-600 mt-6 pt-4 border-t border-slate-700">
            <p>Powered by <span className="text-slate-500">Vexis Talent Agency</span></p>
            <p className="mt-2">Vexis Talent Agency is a trading name of LHA Holdings Ltd.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!interviewStarted) {
    return (
      <div className="min-h-screen bg-slate-950 text-white p-4 py-8" data-testid="consent-screen">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Header - VEXIS Branding */}
          <div className="text-center mb-6 md:mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img 
                src="/vexis-logo.png" 
                alt="VEXIS Talent Agency" 
                className="h-12 md:h-14"
              />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-white mb-1">VEXIS Talent Agency</h1>
            <p className="text-sm md:text-base text-slate-400">Candidate Assessment Platform</p>
          </div>

          {/* Role Info Card */}
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6">
            <div className="flex items-start gap-4 mb-6">
              {inviteData.companyLogoUrl && (
                <img 
                  src={inviteData.companyLogoUrl} 
                  alt={inviteData.companyName || "Company"} 
                  className="h-14 w-14 object-contain rounded-lg bg-white p-1"
                />
              )}
              <div>
                <h2 className="text-xl font-bold text-white">Assessment for {inviteData.roleTitle}</h2>
                {inviteData.companyName && (
                  <p className="text-slate-400 mt-1">Position at {inviteData.companyName}</p>
                )}
              </div>
            </div>

            {/* About Section */}
            <div className="bg-slate-800 border border-slate-600 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white mb-2">About this interview link</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    This interview is delivered through the VEXIS Talent Agency online interview platform. 
                    It runs in your web browser and records video and audio responses using your device 
                    camera and microphone. Interview recordings are stored securely and are only accessible 
                    to authorised VEXIS staff and the hiring team involved in this role.
                  </p>
                </div>
              </div>
            </div>

            {/* Your Details Section */}
            <div className="bg-slate-800 border border-slate-600 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-white flex items-center gap-2 mb-4">
                <User className="h-5 w-5 text-blue-400" />
                Your Details
              </h3>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="candidateName" className="text-slate-300">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="candidateName"
                      type="text"
                      placeholder="Enter your full name"
                      value={candidateName}
                      onChange={(e) => setCandidateName(e.target.value)}
                      className="pl-10 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                      data-testid="input-candidate-name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="candidateEmail" className="text-slate-300">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="candidateEmail"
                      type="email"
                      placeholder="Enter your email address"
                      value={candidateEmail}
                      onChange={(e) => setCandidateEmail(e.target.value)}
                      className="pl-10 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                      data-testid="input-candidate-email"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Camera & Microphone Section */}
            <div className="bg-slate-800 border border-slate-600 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-white flex items-center gap-2 mb-4">
                <Video className="h-5 w-5 text-blue-400" />
                Camera & Microphone Check
              </h3>
              
              <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative border border-slate-700">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  muted 
                  playsInline
                  className="w-full h-full object-cover"
                />
                {cameraPermission === null && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/95">
                    <Button onClick={checkPermissions} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="button-check-permissions">
                      Enable Camera & Microphone
                    </Button>
                  </div>
                )}
                {cameraPermission === false && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/95 gap-4 p-4">
                    <div className="text-center text-white">
                      <VideoOff className="h-12 w-12 mx-auto mb-2 text-red-400" />
                      <p className="font-medium">Camera access denied</p>
                      <p className="text-sm text-slate-400 mt-1">Please allow camera and microphone access to continue</p>
                    </div>
                    <Button onClick={checkPermissions} data-testid="button-retry-permissions" className="bg-blue-600 hover:bg-blue-700 text-white">
                      Retry Permissions
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 md:gap-3 mt-4">
                <Badge 
                  variant={cameraPermission ? "default" : "secondary"} 
                  className={`gap-1.5 text-xs md:text-sm py-1.5 px-3 ${cameraPermission ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 text-white'}`}
                >
                  {cameraPermission ? <Video className="h-3.5 w-3.5" /> : <VideoOff className="h-3.5 w-3.5" />}
                  Camera {cameraPermission ? "Ready" : "Not Ready"}
                </Badge>
                <Badge 
                  variant={micPermission ? "default" : "secondary"} 
                  className={`gap-1.5 text-xs md:text-sm py-1.5 px-3 ${micPermission ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 text-white'}`}
                >
                  {micPermission ? <Mic className="h-3.5 w-3.5" /> : <MicOff className="h-3.5 w-3.5" />}
                  Microphone {micPermission ? "Ready" : "Not Ready"}
                </Badge>
              </div>
            </div>

            {/* Single-Use Link Warning */}
            <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-300 mb-1">Single-Use Link</h3>
                  <p className="text-sm text-amber-200/80">
                    Once you start this interview, this link will be locked to your device. You cannot 
                    share this link or use it on another device.
                  </p>
                </div>
              </div>
            </div>

            {/* Consent Section */}
            <div className="bg-slate-800 border border-slate-600 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-white mb-3">Before we begin</h3>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                By continuing, you agree that Vexis Talent Agency (a trading name of LHA Holdings Ltd) may record, store, and use your video and audio responses for the sole purpose of assessing your suitability for the role you have applied for. Your data will be processed in accordance with the{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline font-medium">NZ Privacy Act 2020</a>{" "}
                and our{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline font-medium">Privacy Policy</a>.
                {" "}Your responses will not be shared with any third party outside of the hiring process. You may request deletion of your data at any time by contacting{" "}
                <a href="mailto:privacy@vexistalentagency.com" className="text-blue-400 hover:text-blue-300 hover:underline font-medium">privacy@vexistalentagency.com</a>.
              </p>
              <div className="flex items-start space-x-3 bg-slate-900 p-4 rounded-lg border border-slate-700">
                <Checkbox 
                  id="consent" 
                  checked={consentGiven}
                  onCheckedChange={(checked) => setConsentGiven(checked === true)}
                  data-testid="checkbox-consent"
                  className="mt-0.5 border-slate-500 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
                <label htmlFor="consent" className="text-sm text-slate-200 cursor-pointer leading-relaxed">
                  I understand and agree to continue.
                </label>
              </div>
            </div>

            {/* Start Interview Button */}
            <Button 
              onClick={claimInvite} 
              disabled={claiming || !consentGiven || !candidateName.trim() || !candidateEmail.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg disabled:bg-slate-700 disabled:text-slate-400"
              size="lg"
              data-testid="button-start-interview"
            >
              {claiming ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {inviteData?.canResume ? "Resuming..." : "Starting..."}
                </>
              ) : inviteData?.canResume ? (
                <>
                  Resume Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-slate-500 space-y-2 pt-4">
            <p>&copy; 2026 Vexis Talent Agency</p>
            <p>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-300 transition-colors">Privacy Policy</a>
              {" · "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-slate-300 transition-colors">Terms & Conditions</a>
            </p>
            <p className="text-slate-600 mt-2">Powered by <span className="text-slate-500">Vexis Talent Agency</span></p>
            <p className="text-slate-600 mt-1">Vexis Talent Agency is a trading name of LHA Holdings Ltd.</p>
          </div>
        </div>
      </div>
    );
  }

  if (showTipsCard) {
    return <InterviewTipsCard onDismiss={() => {
      // Re-unlock in the tips-card gesture as well — for new users this is the closest
      // gesture to when the first question audio plays (more reliable than claimInvite).
      unlockAudioContext();
      setShowTipsCard(false);
    }} />;
  }

  const questions = inviteData.questions || [];
  // Cap currentQuestionIndex to valid range to prevent display issues like "Question 7 of 5"
  const safeQuestionIndex = Math.min(currentQuestionIndex, Math.max(0, questions.length - 1));
  const currentQuestion = questions[safeQuestionIndex];
  const progress = ((uploadedQuestions.size) / questions.length) * 100;
  const hasRecording = recordedBlobs.length > 0;
  const isUploaded = uploadedQuestions.has(safeQuestionIndex);
  // Show submit when the candidate is on the final question and has uploaded it.
  // Using per-question membership rather than total size so the button appears
  // correctly both for fresh sessions and for resumes where the client started
  // partway through and uploadedQuestions only contains this session's uploads.
  const onLastQuestion = safeQuestionIndex === questions.length - 1;
  const allUploaded = onLastQuestion && isUploaded;

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:px-6 lg:px-10" data-testid="interview-screen">
      <div className="max-w-4xl md:max-w-[900px] lg:max-w-[1200px] xl:max-w-[1280px] mx-auto space-y-4 md:space-y-6">
        {/* Header */}
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src="/vexis-logo.png" alt="VEXIS" className="h-8 w-auto" />
              <div className="border-l border-slate-600 pl-3 ml-1">
                <h1 className="text-sm md:text-base font-bold text-white">{inviteData.roleTitle}</h1>
                <p className="text-xs text-slate-400">Structured Candidate Assessment</p>
              </div>
            </div>
            <Badge className="gap-1.5 text-xs md:text-sm w-fit bg-blue-600 text-white border-0">
              <Clock className="h-3.5 w-3.5" />
              Question {safeQuestionIndex + 1} of {questions.length}
            </Badge>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-slate-800 rounded-full h-2.5 overflow-hidden">
          <div 
            className="bg-blue-500 h-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="grid md:grid-cols-[2fr_3fr] lg:grid-cols-[7fr_13fr] gap-6">
          {/* Question Card */}
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6">
            <div className="mb-4">
              <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                Question {safeQuestionIndex + 1}
                {audioNarrateLoading && (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                )}
              </h2>
              <Badge className="bg-slate-700 text-slate-300 border-0">
                {currentQuestion?.questionType}
              </Badge>
            </div>
            
            {isPlayingAudio && (
              <div className="flex items-center gap-2 mb-4 text-blue-400 animate-pulse">
                <Volume2 className="h-5 w-5" />
                <span className="text-sm font-medium">Playing question audio...</span>
              </div>
            )}
            
            {audioAutoplayBlocked && !isPlayingAudio && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-slate-800 border border-slate-600 rounded-lg">
                <Volume2 className="h-5 w-5 text-blue-400" />
                <span className="text-sm text-slate-300 flex-1">Click to hear the question read aloud</span>
                <Button
                  size="sm"
                  onClick={() => {
                    if (currentQuestion?.questionText) {
                      replayQuestionAudio(currentQuestion.questionText, currentQuestionIndex, currentQuestion.id);
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="button-play-question"
                >
                  <Volume2 className="h-4 w-4 mr-1" />
                  Play
                </Button>
              </div>
            )}

            {ttsUnavailable && !isPlayingAudio && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-400" data-testid="tts-unavailable-notice">
                <VolumeX className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">Audio is temporarily unavailable — please read the question above.</span>
              </div>
            )}
            
            <p className="text-slate-200 text-lg leading-relaxed lg:leading-[1.7]">
              {currentQuestion?.questionText}
            </p>
            
            {currentQuestion?.timeLimit && (
              <p className="mt-4 text-sm text-slate-400 flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Recording time: {(() => {
                  const t = currentQuestion.timeLimit;
                  const mins = Math.floor(t / 60);
                  const secs = t % 60;
                  if (mins === 0) return `${secs} second${secs !== 1 ? 's' : ''}`;
                  if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
                  return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
                })()}
              </p>
            )}
            
            {!isRecording && !hasRecording && !isUploaded && (
              <div className="mt-4 pt-4 border-t border-slate-700 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (currentQuestion?.questionText) {
                      replayQuestionAudio(currentQuestion.questionText, currentQuestionIndex, currentQuestion.id);
                    }
                  }}
                  disabled={isPlayingAudio}
                  className="text-xs gap-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="button-replay-audio"
                >
                  <Volume2 className="h-3 w-3" />
                  {isPlayingAudio ? "Playing..." : "Replay Question"}
                </Button>
                {!repeatedQuestions.has(currentQuestionIndex) ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRepeatedQuestions(prev => {
                        const newSet = new Set(Array.from(prev));
                        newSet.add(currentQuestionIndex);
                        return newSet;
                      });
                      setShowingRepeat(true);
                      setTimeout(() => setShowingRepeat(false), 3000);
                    }}
                    className="text-xs gap-1 text-slate-500 hover:text-slate-300"
                    data-testid="button-repeat-question"
                  >
                    View Transcript
                  </Button>
                ) : (
                  <p className="text-xs text-slate-500 italic">
                    Transcript already viewed
                  </p>
                )}
                {showingRepeat && (
                  <div className="mt-2 p-2 bg-blue-900/30 border border-blue-600/50 rounded text-sm text-blue-300">
                    The question text is displayed above. Take a moment to review before recording.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Video Response Card */}
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6">
            <div className="mb-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Video className="h-5 w-5 text-blue-400" />
                Your Response
              </h2>
            </div>
            
            <div className="aspect-video bg-slate-800 rounded-lg overflow-hidden relative border border-slate-600 md:min-h-[320px] lg:min-h-[420px] xl:min-h-[480px]">
              <video 
                ref={videoRef} 
                autoPlay 
                muted 
                playsInline
                className="w-full h-full object-cover"
              />
              {isRecording && (
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-full animate-pulse">
                  <span className="h-2 w-2 bg-white rounded-full" />
                  Recording
                </div>
              )}
              {isUploaded && (
                <div className="absolute inset-0 bg-green-900/90 flex items-center justify-center">
                  <div className="text-center text-white">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-2" />
                    <p className="font-medium">Response Uploaded</p>
                  </div>
                </div>
              )}
              {cameraPermission === false && !isUploaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/95 gap-4 p-4">
                  <div className="text-center text-white">
                    <VideoOff className="h-12 w-12 mx-auto mb-2 text-red-400" />
                    <p className="font-medium">Camera not available</p>
                    <p className="text-sm text-slate-400 mt-1">Click below to enable camera access</p>
                  </div>
                  <Button onClick={setupMedia} data-testid="button-enable-camera" className="bg-blue-600 hover:bg-blue-700 text-white">
                    Enable Camera
                  </Button>
                </div>
              )}
            </div>

            {/* Status indicator */}
            <div className="text-center py-4" data-testid="interview-status">
              {isPlayingAudio && (
                <div className="flex items-center justify-center gap-2 text-blue-400">
                  <Volume2 className="h-5 w-5 animate-pulse" />
                  <span className="font-medium">Please listen carefully to the question...</span>
                </div>
              )}
              
              {audioAutoplayBlocked && !isPlayingAudio && !isRecording && !isUploaded && (
                <div className="space-y-2">
                  <p className="text-amber-400">Click to hear your question:</p>
                  <Button 
                    onClick={() => {
                      if (inviteData?.questions?.[currentQuestionIndex]) {
                        const q = inviteData.questions[currentQuestionIndex];
                        replayQuestionAudio(q.questionText, currentQuestionIndex, q.id);
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="button-play-question"
                  >
                    <Volume2 className="mr-2 h-4 w-4" />
                    Play Question
                  </Button>
                </div>
              )}
              
              {isRecording && recordingTimeLeft !== null && (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 text-red-400">
                    <span className="h-3 w-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="font-medium">Recording in progress</span>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {Math.floor(recordingTimeLeft / 60)}:{(recordingTimeLeft % 60).toString().padStart(2, '0')}
                  </div>
                  <p className="text-xs text-slate-500">Take your time to give a thoughtful response</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={stopRecording}
                    className="border-slate-500 text-slate-300 hover:bg-slate-700 hover:text-white"
                    data-testid="button-finish-early"
                  >
                    Finish Answer Early
                  </Button>
                </div>
              )}
              
              {uploading && (
                <div className="flex items-center justify-center gap-2 text-blue-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="font-medium">Saving your response{uploadAttempts > 1 ? ` (attempt ${uploadAttempts}/${maxRetries})` : ''}...</span>
                </div>
              )}
              
              {uploadError && !uploading && !isUploaded && (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 text-red-400">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Connection issue detected</span>
                  </div>
                  <p className="text-sm text-slate-400">Your progress has been saved. We encountered a problem uploading your last answer - please check your internet connection and click Retry. If the problem continues, your interview can be resumed from where you left off.</p>
                  <Button 
                    onClick={retryUpload} 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="button-retry-upload"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry upload
                  </Button>
                </div>
              )}
              
              {recordingError && !isRecording && !isUploaded && (
                <div className="space-y-3" data-testid="recording-error">
                  <div className="flex items-center justify-center gap-2 text-red-400">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Recording problem detected</span>
                  </div>
                  <p className="text-sm text-slate-400">{recordingError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRecordingError(null);
                      setRecordedBlobs([]);
                      recordingInitiatedRef.current.delete(currentQuestionIndex);
                    }}
                    className="border-slate-500 text-slate-300 hover:bg-slate-700 hover:text-white"
                    data-testid="button-retry-recording"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Try Recording Again
                  </Button>
                </div>
              )}

              {isUploaded && safeQuestionIndex < questions.length - 1 && (
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Response saved. Moving to next question...</span>
                </div>
              )}
              
              {isUploaded && safeQuestionIndex === questions.length - 1 && (
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">All responses saved. Ready to submit when you are.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit Card */}
        {allUploaded && (
          <div className="bg-green-900/30 border border-green-600/50 rounded-xl p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
                <div>
                  <h3 className="font-semibold text-green-300">Assessment complete</h3>
                  <p className="text-sm text-green-400/80">All questions answered — your responses are saved. Click Submit when you're ready.</p>
                </div>
              </div>
              <Button 
                onClick={completeInterview}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                data-testid="button-submit"
              >
                Submit Assessment
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-600 pt-4">
          <p>Powered by <span className="text-slate-500">Vexis Talent Agency</span></p>
          <p className="mt-1">Vexis Talent Agency is a trading name of LHA Holdings Ltd.</p>
        </div>
      </div>
    </div>
  );
}
